import React, { useEffect, useState } from 'react';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdSearch, MdChevronLeft, MdChevronRight } from 'react-icons/md';

interface FormTemplate {
    _id: string;
    name: string;
    industry?: string;
    subIndustry?: string;
    isGeneric?: boolean;
    isActive?: boolean;
    templateSlug?: string;
    minimumPrice?: number;
    createdAt: string;
    user?: { _id: string; email: string; name?: string; surname?: string; website?: string } | null;
    landingPages?: string[];
}

interface Stats {
    byIndustry: { industry: string; count: number }[];
    bySubIndustry: { subIndustry: string; industry: string; count: number }[];
}

const PERIODS = [
    { label: '7d', value: '7' },
    { label: '14d', value: '14' },
    { label: '30d', value: '30' },
    { label: '90d', value: '90' },
    { label: 'All time', value: 'all' },
];

export default function BackOfficeFormTemplates() {
    const [templates, setTemplates] = useState<FormTemplate[]>([]);
    const [stats, setStats] = useState<Stats | null>(null);
    const [loading, setLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(true);
    const [period, setPeriod] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterIndustry, setFilterIndustry] = useState('all');
    const [filterGeneric, setFilterGeneric] = useState('all');
    const [filterLandingPage, setFilterLandingPage] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [industries, setIndustries] = useState<string[]>([]);
    const limit = 50;

    useEffect(() => {
        fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period]);

    useEffect(() => {
        fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, searchTerm, filterIndustry, filterGeneric, filterLandingPage]);

    const fetchStats = async () => {
        try {
            setStatsLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${CONFIG.apiUrl}/admin/form-templates/stats`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { days: period },
            });
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setStatsLoading(false);
        }
    };

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const params: any = { page: currentPage, limit };
            if (searchTerm) params.search = searchTerm;
            if (filterIndustry !== 'all') params.industry = filterIndustry;
            if (filterGeneric !== 'all') params.isGeneric = filterGeneric;
            if (filterLandingPage !== 'all') params.hasLandingPage = filterLandingPage;

            const response = await axios.get(`${CONFIG.apiUrl}/admin/form-templates`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });

            setTemplates(response.data.templates);
            setTotal(response.data.total);
            setTotalPages(response.data.pages);

            if (filterIndustry === 'all' && filterGeneric === 'all' && !searchTerm && currentPage === 1) {
                const uniqueIndustries = [...new Set(
                    response.data.templates.map((t: FormTemplate) => t.industry).filter(Boolean)
                )] as string[];
                setIndustries(uniqueIndustries);
            }
        } catch (error) {
            console.error('Error fetching form templates:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value: string) => { setSearchTerm(value); setCurrentPage(1); };
    const handleFilterIndustry = (value: string) => { setFilterIndustry(value); setCurrentPage(1); };
    const handleFilterGeneric = (value: string) => { setFilterGeneric(value); setCurrentPage(1); };
    const handleFilterLandingPage = (value: string) => { setFilterLandingPage(value); setCurrentPage(1); };

    const maxIndustryCount = stats?.byIndustry[0]?.count ?? 1;
    const maxSubIndustryCount = stats?.bySubIndustry[0]?.count ?? 1;

    return (
        <div className="space-y-5">

            {/* ── Charts ── */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {[0, 1].map((col) => (
                    <Card key={col} extra="p-5">
                        {col === 0 && (
                            <>
                                <div className="mb-4 flex items-center justify-between">
                                    <div className="text-sm font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Top Industries
                                    </div>
                                    {/* Period selector only on the first card */}
                                    <div className="flex gap-1">
                                        {PERIODS.map((p) => (
                                            <button
                                                key={p.value}
                                                onClick={() => setPeriod(p.value)}
                                                className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                                                    period === p.value
                                                        ? 'bg-brand-500 text-white'
                                                        : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-navy-700 dark:text-gray-400'
                                                }`}
                                            >
                                                {p.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {statsLoading ? (
                                    <div className="space-y-3">
                                        {[...Array(5)].map((_, i) => (
                                            <div key={i} className="h-6 animate-pulse rounded bg-gray-100 dark:bg-navy-700" />
                                        ))}
                                    </div>
                                ) : !stats?.byIndustry.length ? (
                                    <div className="py-6 text-center text-sm text-gray-400">No data for this period</div>
                                ) : (
                                    <div className="space-y-3">
                                        {stats.byIndustry.map((row) => (
                                            <div key={row.industry}>
                                                <div className="mb-1 flex items-center justify-between text-sm">
                                                    <span className="truncate max-w-[75%] font-medium text-navy-700 dark:text-white">
                                                        {row.industry}
                                                    </span>
                                                    <span className="text-gray-500 dark:text-gray-400">{row.count}</span>
                                                </div>
                                                <div className="h-1.5 w-full rounded-full bg-gray-100 dark:bg-navy-700">
                                                    <div
                                                        className="h-1.5 rounded-full bg-brand-500 transition-all"
                                                        style={{ width: `${(row.count / maxIndustryCount) * 100}%` }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                        {col === 1 && (
                            <>
                                <div className="mb-4 flex items-center justify-between">
                                    <div className="text-sm font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">
                                        Top Sub-Industries
                                    </div>
                                    <div className="text-xs text-gray-400 dark:text-gray-500">
                                        {PERIODS.find((p) => p.value === period)?.label}
                                    </div>
                                </div>
                                {statsLoading ? (
                                    <div className="space-y-3">
                                        {[...Array(5)].map((_, i) => (
                                            <div key={i} className="h-6 animate-pulse rounded bg-gray-100 dark:bg-navy-700" />
                                        ))}
                                    </div>
                                ) : !stats?.bySubIndustry.length ? (
                                    <div className="py-6 text-center text-sm text-gray-400">No data for this period</div>
                                ) : (
                                    <div className="space-y-3">
                                        {stats.bySubIndustry.map((row) => (
                                            <div key={row.subIndustry}>
                                                <div className="mb-1 flex items-center justify-between text-sm">
                                                    <span className="truncate max-w-[75%] font-medium text-navy-700 dark:text-white">
                                                        {row.subIndustry}
                                                        {row.industry && (
                                                            <span className="ml-1 text-xs font-normal text-gray-400">
                                                                ({row.industry})
                                                            </span>
                                                        )}
                                                    </span>
                                                    <span className="text-gray-500 dark:text-gray-400">{row.count}</span>
                                                </div>
                                                <div className="h-1.5 w-full rounded-full bg-gray-100 dark:bg-navy-700">
                                                    <div
                                                        className="h-1.5 rounded-full bg-indigo-500 transition-all"
                                                        style={{ width: `${(row.count / maxSubIndustryCount) * 100}%` }}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </Card>
                ))}
            </div>

            {/* ── Table ── */}
            <Card extra="p-5">
                <div className="mb-5 flex items-center justify-between gap-2">
                    <div className="text-2xl font-bold text-navy-700 dark:text-white">
                        Form Templates
                        {!loading && (
                            <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                                ({total} total)
                            </span>
                        )}
                    </div>
                </div>

                <div className="mb-5 flex flex-wrap gap-2">
                    <div className="relative flex-1 min-w-[200px]">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <MdSearch className="text-gray-400" />
                        </div>
                        <input
                            type="text"
                            placeholder="Search by name, industry, sub-industry, owner (name, company, email), landing page..."
                            value={searchTerm}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearch(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                        />
                    </div>
                    <select
                        value={filterIndustry}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterIndustry(e.target.value)}
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                    >
                        <option value="all">All Industries</option>
                        {industries.map((ind) => (
                            <option key={ind} value={ind}>{ind}</option>
                        ))}
                    </select>
                    <select
                        value={filterGeneric}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterGeneric(e.target.value)}
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                    >
                        <option value="all">All Types</option>
                        <option value="true">Generic</option>
                        <option value="false">Custom</option>
                    </select>
                    <select
                        value={filterLandingPage}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleFilterLandingPage(e.target.value)}
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-800 dark:text-white"
                    >
                        <option value="all">All</option>
                        <option value="true">With Landing Page</option>
                    </select>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Name</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Industry</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Sub-Industry</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">User Email</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Website</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Landing Pages</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Type</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Min Price</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Status</th>
                                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wide text-gray-600 dark:text-gray-400">Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={10} className="py-10 text-center text-gray-500">Loading...</td></tr>
                            ) : templates.length === 0 ? (
                                <tr><td colSpan={10} className="py-10 text-center text-gray-500">No form templates found</td></tr>
                            ) : (
                                templates.map((t) => (
                                    <tr key={t._id} className="border-b border-gray-100 dark:border-navy-700">
                                        <td className="py-3 text-sm font-medium text-navy-700 dark:text-white">
                                            {t.name}
                                            {t.templateSlug && (
                                                <span className="ml-1 text-xs text-gray-400">({t.templateSlug})</span>
                                            )}
                                        </td>
                                        <td className="py-3 text-sm text-gray-600 dark:text-gray-400">
                                            {t.industry || <span className="text-gray-400">-</span>}
                                        </td>
                                        <td className="py-3 text-sm text-gray-600 dark:text-gray-400">
                                            {t.subIndustry || <span className="text-gray-400">-</span>}
                                        </td>
                                        <td className="py-3 text-sm text-gray-600 dark:text-gray-400">
                                            {t.user?.email || <span className="text-gray-400">-</span>}
                                        </td>
                                        <td className="py-3 text-sm text-gray-600 dark:text-gray-400">
                                            {t.user?.website
                                                ? <a href={t.user.website} target="_blank" rel="noopener noreferrer" className="text-brand-500 hover:underline">{t.user.website}</a>
                                                : <span className="text-gray-400">-</span>}
                                        </td>
                                        <td className="py-3 text-sm text-gray-600 dark:text-gray-400 max-w-[200px]">
                                            {t.landingPages?.length
                                                ? <div className="flex flex-wrap gap-1">{[...t.landingPages].sort().map((lp: string) => (
                                                    <span key={lp} title={lp} className="inline-block max-w-[180px] truncate rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-navy-700 dark:text-gray-300">{lp}</span>
                                                ))}</div>
                                                : <span className="text-gray-400">-</span>}
                                        </td>
                                        <td className="py-3">
                                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                                                t.isGeneric
                                                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                                    : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                            }`}>
                                                {t.isGeneric ? 'Generic' : 'Custom'}
                                            </span>
                                        </td>
                                        <td className="py-3 text-sm text-gray-600 dark:text-gray-400">
                                            {t.minimumPrice != null ? `${t.minimumPrice} €` : <span className="text-gray-400">-</span>}
                                        </td>
                                        <td className="py-3">
                                            <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${
                                                t.isActive !== false
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                                            }`}>
                                                {t.isActive !== false ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="py-3 text-sm text-gray-600 dark:text-gray-400">
                                            {new Date(t.createdAt).toLocaleDateString()}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {!loading && totalPages > 1 && (
                    <div className="mt-5 flex items-center justify-between border-t border-gray-200 pt-4 dark:border-navy-600">
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                            Page {currentPage} of {totalPages} ({total} total)
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(p => p - 1)}
                                disabled={currentPage === 1}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                            >
                                <MdChevronLeft /> Previous
                            </button>
                            <div className="flex items-center gap-1">
                                {[...Array(totalPages)].map((_, index) => {
                                    const p = index + 1;
                                    if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)) {
                                        return (
                                            <button
                                                key={p}
                                                onClick={() => setCurrentPage(p)}
                                                className={`min-w-[2.5rem] rounded-lg px-3 py-2 text-sm font-medium ${
                                                    currentPage === p
                                                        ? 'bg-brand-500 text-white'
                                                        : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700'
                                                }`}
                                            >
                                                {p}
                                            </button>
                                        );
                                    } else if (p === currentPage - 2 || p === currentPage + 2) {
                                        return <span key={p} className="px-2 text-gray-500">...</span>;
                                    }
                                    return null;
                                })}
                            </div>
                            <button
                                onClick={() => setCurrentPage(p => p + 1)}
                                disabled={currentPage === totalPages}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-navy-600 dark:bg-navy-800 dark:text-white dark:hover:bg-navy-700"
                            >
                                Next <MdChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}
